import React, { useState, useEffect, useRef } from 'react';

interface Option {
  id: number;
  name: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: number | number[] | '';
  onChange: (value: any) => void;
  placeholder?: string;
  multiple?: boolean;
  label?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Selecione...", 
  multiple = false,
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option => 
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option: Option) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      if (currentValues.includes(option.id)) {
        onChange(currentValues.filter(id => id !== option.id));
      } else {
        onChange([...currentValues, option.id]);
      }
    } else {
      onChange(option.id);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleRemove = (e: React.MouseEvent, idToRemove: number) => {
    e.stopPropagation();
    if (Array.isArray(value)) {
      onChange(value.filter(id => id !== idToRemove));
    }
  };

  const getDisplayValue = () => {
    if (multiple) {
      if (!Array.isArray(value) || value.length === 0) return '';
      return `${value.length} selecionado(s)`;
    }
    if (!value) return '';
    const selected = options.find(o => o.id === value);
    return selected ? selected.name : '';
  };

  return (
    <div className="form-group" ref={wrapperRef}>
      {label && <label>{label}</label>}
      
      <div 
        style={{ 
          position: 'relative', 
          border: '1px solid var(--border-color)', 
          borderRadius: '8px', 
          backgroundColor: '#fff',
          minHeight: '42px'
        }}
      >
        {/* Área de Visualização / Input */}
        <div 
          onClick={() => setIsOpen(!isOpen)}
          style={{ 
            padding: '8px 12px', 
            cursor: 'pointer', 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '5px',
            alignItems: 'center'
          }}
        >
          {multiple && Array.isArray(value) && value.map(id => {
            const option = options.find(o => o.id === id);
            if (!option) return null;
            return (
              <span key={id} style={{ 
                background: 'var(--primary-light)', 
                color: 'var(--primary-blue)', 
                padding: '2px 8px', 
                borderRadius: '12px', 
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                {option.name}
                <span 
                  onClick={(e) => handleRemove(e, id)}
                  style={{ cursor: 'pointer', fontWeight: 'bold' }}
                >
                  &times;
                </span>
              </span>
            );
          })}

          {(!multiple || (Array.isArray(value) && value.length === 0)) && (
            <span style={{ color: value ? '#333' : '#999' }}>
              {getDisplayValue() || placeholder}
            </span>
          )}
          
          <span style={{ marginLeft: 'auto', color: '#999', fontSize: '0.8rem' }}>
            {isOpen ? '▲' : '▼'}
          </span>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div style={{ 
            position: 'absolute', 
            top: '100%', 
            left: 0, 
            right: 0, 
            zIndex: 1000, 
            background: 'white', 
            border: '1px solid var(--border-color)', 
            borderRadius: '8px', 
            marginTop: '5px', 
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            maxHeight: '250px',
            overflowY: 'auto'
          }}>
            <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar..."
                autoFocus
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  outline: 'none'
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '10px', color: '#999', textAlign: 'center' }}>
                Nenhum resultado encontrado
              </div>
            ) : (
              filteredOptions.map(option => {
                const isSelected = multiple 
                  ? (Array.isArray(value) && value.includes(option.id))
                  : value === option.id;

                return (
                  <div 
                    key={option.id}
                    onClick={() => handleSelect(option)}
                    style={{ 
                      padding: '10px 15px', 
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#f0f9ff' : 'white',
                      color: isSelected ? 'var(--primary-blue)' : '#333',
                      borderBottom: '1px solid #f5f5f5'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#f0f9ff' : 'white'}
                  >
                    {option.name}
                    {isSelected && <span style={{ float: 'right' }}>✓</span>}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableSelect;
